import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Coins, Shield, Settings, Zap, AlertTriangle, CheckCircle, ExternalLink, Globe } from 'lucide-react';
import api from '../../lib/api';
import { useBlockchainNetworks, getExplorerTokenUrl } from '../../hooks/useTokenization';

interface TokenConfig {
  tokenStandard: string;
  network: string;
  contractAddress: string | null;
  isDeployed: boolean;
  isTransferable: boolean;
  requiresWhitelist: boolean;
  transferFee: number;
  mintPolicy: string;
  burnPolicy: string;
  pausable: boolean;
  isPaused: boolean;
}

interface Asset {
  id: string;
  name: string;
  tokenSymbol: string;
  tokenName: string;
  tokenDecimals: number;
  initialSupply: number;
  maxSupply: number | null;
  unitPrice: number;
  status: string;
  tokenConfig: TokenConfig | null;
}

export function TokenConfiguration() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [config, setConfig] = useState({
    tokenStandard: 'ERC20',
    network: 'POLYGON',
    isTransferable: true,
    requiresWhitelist: true,
    transferFee: 0,
    mintPolicy: 'APPROVAL_REQUIRED',
    burnPolicy: 'APPROVAL_REQUIRED',
    pausable: true,
  });

  // Fetch blockchain networks
  const { data: networks = [] } = useBlockchainNetworks();

  const { data: asset, isLoading } = useQuery({
    queryKey: ['asset', id],
    queryFn: async () => {
      const res = await api.get(`/tokenization/assets/${id}`);
      return res.data as Asset;
    },
    enabled: !!id,
  });

  // Deploy token mutation
  const deployMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/tokenization/assets/${id}/deploy`, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
    },
  });

  // Update config mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<TokenConfig>) => {
      return api.put(`/tokenization/assets/${id}/token-config`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
    },
  });

  // Pause/Unpause mutation
  const pauseMutation = useMutation({
    mutationFn: async (pause: boolean) => {
      return api.post(`/tokenization/assets/${id}/${pause ? 'pause' : 'unpause'}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
    },
  });

  // Query para listar ativos quando não há ID selecionado
  const { data: assets, isLoading: assetsLoading } = useQuery<Asset[]>({
    queryKey: ['tokenization-assets-for-config'],
    queryFn: async () => {
      const res = await api.get('/tokenization/assets', {
        params: { status: 'APPROVED,MINTED,ACTIVE' }
      });
      return res.data.data || res.data;
    },
    enabled: !id,
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

  if (!id) {
    // Mostrar lista de ativos para selecionar
    if (assetsLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Configuração de Token</h1>
          <p className="text-surface-400 mt-1">Selecione um ativo para configurar ou implantar o token na blockchain</p>
        </div>

        {!assets || assets.length === 0 ? (
          <div className="bg-surface-800 rounded-xl p-8 text-center">
            <Settings className="w-12 h-12 text-surface-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-surface-200 mb-2">Nenhum ativo disponível</h3>
            <p className="text-surface-400 mb-4">
              Você precisa ter ativos aprovados para configurar tokens.
            </p>
            <button 
              onClick={() => navigate('/admin/tokenization/assets')}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Ver Ativos
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {assets.map((a) => (
              <div 
                key={a.id} 
                className="bg-surface-800 rounded-xl p-4 hover:bg-surface-700/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/admin/tokenization/token-config/${a.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
                      <Settings className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-surface-100">{a.name}</h3>
                      <p className="text-sm text-surface-400">{a.tokenSymbol || 'Símbolo não definido'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-surface-200">{formatCurrency(a.unitPrice)}/token</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      a.tokenConfig?.isDeployed 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {a.tokenConfig?.isDeployed ? 'Implantado' : 'Não implantado'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-400">Ativo não encontrado</p>
        <button 
          onClick={() => navigate('/admin/tokenization/token-config')}
          className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          Voltar para lista
        </button>
      </div>
    );
  }

  const isDeployed = asset.tokenConfig?.isDeployed;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Configuração do Token</h1>
          <p className="text-surface-400">{asset.name} ({asset.tokenSymbol})</p>
        </div>
      </div>

      {/* Status Banner */}
      {isDeployed ? (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <div>
                <p className="font-medium text-green-400">Token Implantado</p>
                <p className="text-sm text-surface-400">
                  Contrato: {asset.tokenConfig?.contractAddress}
                </p>
              </div>
            </div>
            {asset.tokenConfig?.contractAddress && asset.tokenConfig?.network && (
              <a
                href={getExplorerTokenUrl(asset.tokenConfig.network, asset.tokenConfig.contractAddress, networks)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300"
              >
                Ver no Explorer
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-500" />
            <div>
              <p className="font-medium text-yellow-400">Token Não Implantado</p>
              <p className="text-sm text-surface-400">
                Configure e implante o token para começar a negociar
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Token Standard & Network */}
        <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
          <div className="flex items-center gap-2 mb-4">
            <Coins className="w-5 h-5 text-primary-400" />
            <h2 className="text-lg font-semibold">Padrão do Token</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-surface-400 mb-1">Padrão</label>
              <select
                value={config.tokenStandard}
                onChange={(e) => setConfig({ ...config, tokenStandard: e.target.value })}
                disabled={isDeployed}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <option value="ERC20">ERC-20 (Fungível Simples)</option>
                <option value="ERC1400">ERC-1400 (Security Token)</option>
                <option value="ERC4626">ERC-4626 (Vault Tokenizado)</option>
              </select>
              <p className="text-xs text-surface-500 mt-1">
                {config.tokenStandard === 'ERC20' && 'Token fungível padrão, ideal para stablecoins e tokens simples'}
                {config.tokenStandard === 'ERC1400' && 'Token de segurança com controles de compliance integrados'}
                {config.tokenStandard === 'ERC4626' && 'Token vault com rendimento automático'}
              </p>
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Rede Blockchain</label>
              <select
                value={config.network}
                onChange={(e) => setConfig({ ...config, network: e.target.value })}
                disabled={isDeployed}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 disabled:opacity-50"
              >
                <optgroup label="Mainnets">
                  {networks.filter(n => !n.config.isTestnet).map(({ network, config: netConfig }) => (
                    <option key={network} value={network}>
                      {netConfig.name} (Chain ID: {netConfig.chainId})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Testnets">
                  {networks.filter(n => n.config.isTestnet).map(({ network, config: netConfig }) => (
                    <option key={network} value={network}>
                      {netConfig.name} (Chain ID: {netConfig.chainId})
                    </option>
                  ))}
                </optgroup>
              </select>
              {config.network && networks.find(n => n.network === config.network) && (
                <div className="mt-2 p-2 bg-surface-800/50 rounded-lg">
                  <div className="flex items-center gap-2 text-xs text-surface-400">
                    <Globe className="w-3 h-3" />
                    <span>
                      {networks.find(n => n.network === config.network)?.config.nativeCurrency.symbol} • 
                      {networks.find(n => n.network === config.network)?.config.isTestnet ? ' Testnet' : ' Mainnet'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-surface-700">
              <div>
                <p className="text-sm text-surface-400">Nome</p>
                <p className="font-medium">{asset.tokenName}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Símbolo</p>
                <p className="font-medium">{asset.tokenSymbol}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Decimais</p>
                <p className="font-medium">{asset.tokenDecimals}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Supply Inicial</p>
                <p className="font-medium">{asset.initialSupply.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Transfer Settings */}
        <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-primary-400" />
            <h2 className="text-lg font-semibold">Configurações de Transferência</h2>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium">Transferível</p>
                <p className="text-sm text-surface-400">Permite transferências entre carteiras</p>
              </div>
              <input
                type="checkbox"
                checked={config.isTransferable}
                onChange={(e) => setConfig({ ...config, isTransferable: e.target.checked })}
                disabled={isDeployed}
                className="w-5 h-5 rounded"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium">Whitelist Obrigatória</p>
                <p className="text-sm text-surface-400">Apenas carteiras aprovadas podem receber</p>
              </div>
              <input
                type="checkbox"
                checked={config.requiresWhitelist}
                onChange={(e) => setConfig({ ...config, requiresWhitelist: e.target.checked })}
                className="w-5 h-5 rounded"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium">Pausável</p>
                <p className="text-sm text-surface-400">Permite pausar todas as transferências</p>
              </div>
              <input
                type="checkbox"
                checked={config.pausable}
                onChange={(e) => setConfig({ ...config, pausable: e.target.checked })}
                disabled={isDeployed}
                className="w-5 h-5 rounded"
              />
            </label>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Taxa de Transferência (%)</label>
              <input
                type="number"
                value={config.transferFee}
                onChange={(e) => setConfig({ ...config, transferFee: parseFloat(e.target.value) || 0 })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2"
                min={0}
                max={10}
                step={0.1}
              />
            </div>
          </div>
        </div>

        {/* Supply Policies */}
        <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-primary-400" />
            <h2 className="text-lg font-semibold">Políticas de Supply</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-surface-400 mb-1">Política de Mint</label>
              <select
                value={config.mintPolicy}
                onChange={(e) => setConfig({ ...config, mintPolicy: e.target.value })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2"
              >
                <option value="OPEN">Aberto (sem restrições)</option>
                <option value="APPROVAL_REQUIRED">Requer Aprovação</option>
                <option value="CUSTODY_REQUIRED">Requer Custódia Verificada</option>
                <option value="DISABLED">Desabilitado</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Política de Burn</label>
              <select
                value={config.burnPolicy}
                onChange={(e) => setConfig({ ...config, burnPolicy: e.target.value })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2"
              >
                <option value="OPEN">Aberto (sem restrições)</option>
                <option value="APPROVAL_REQUIRED">Requer Aprovação</option>
                <option value="REDEMPTION_ONLY">Apenas via Resgate</option>
                <option value="DISABLED">Desabilitado</option>
              </select>
            </div>

            {asset.maxSupply && (
              <div className="pt-4 border-t border-surface-700">
                <p className="text-sm text-surface-400">Supply Máximo</p>
                <p className="font-medium">{asset.maxSupply.toLocaleString()} tokens</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-primary-400" />
            <h2 className="text-lg font-semibold">Ações</h2>
          </div>

          <div className="space-y-4">
            {!isDeployed ? (
              <button
                onClick={() => deployMutation.mutate()}
                disabled={deployMutation.isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <Coins className="w-5 h-5" />
                {deployMutation.isPending ? 'Implantando...' : 'Implantar Token'}
              </button>
            ) : (
              <>
                <button
                  onClick={() => updateMutation.mutate({
                    requiresWhitelist: config.requiresWhitelist,
                    transferFee: config.transferFee,
                    mintPolicy: config.mintPolicy,
                    burnPolicy: config.burnPolicy,
                  })}
                  disabled={updateMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Settings className="w-5 h-5" />
                  {updateMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
                </button>

                {config.pausable && (
                  <button
                    onClick={() => pauseMutation.mutate(!asset.tokenConfig?.isPaused)}
                    disabled={pauseMutation.isPending}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors disabled:opacity-50 ${
                      asset.tokenConfig?.isPaused
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {asset.tokenConfig?.isPaused ? 'Retomar Transferências' : 'Pausar Transferências'}
                  </button>
                )}
              </>
            )}
          </div>

          {deployMutation.isError && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">Erro ao implantar token. Tente novamente.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TokenConfiguration;
